import {
  ArrowRight,
  Check,
  MessageCircle,
  Search,
  ShieldCheck,
  Sparkles,
  Wrench,
} from "lucide-react";
import { Link, Outlet, createFileRoute, useRouterState } from "@tanstack/react-router";
import { ContactSection } from "@/components/contact-section";
import { PublicSiteHeader } from "@/components/public-site-header";
import { canonicalSiteUrl } from "@/config";

const services = [
  {
    title: "Website design and build",
    body: "Company websites, service websites and landing pages planned around clear messaging and mobile enquiries.",
    icon: Sparkles,
    href: "/en/services/web-design",
  },
  {
    title: "SEO search optimisation",
    body: "Keywords, site structure, page titles, FAQs and content direction that people and Google can understand.",
    icon: Search,
    href: "/en/services/seo",
  },
  {
    title: "Google Ads search management",
    body: "Keyword intent, search ads, landing pages and enquiry tracking built around what customers are actively looking for.",
    icon: Search,
    href: "/en/services/google-ads",
  },
  {
    title: "Meta Ads management",
    body: "Facebook, Instagram and Threads campaigns connecting audience, creative direction, remarketing and clear enquiry actions.",
    icon: MessageCircle,
    href: "/en/services/meta-ads",
  },
  {
    title: "Custom ERP systems",
    body: "Custom operations systems for customer, sales, order, inventory, project, employee, attendance, roster, payroll and approval workflows.",
    icon: Wrench,
    href: "/en/services/erp-system-development",
  },
  {
    title: "WhatsApp chatbot customisation",
    body: "Automated replies, FAQ, lead collection, booking and human handover organised into a clear WhatsApp conversation flow.",
    icon: MessageCircle,
    href: "/en/services/whatsapp-chatbot-development",
  },
  {
    title: "Website redesign",
    body: "A clearer page order, service explanation and mobile reading experience for an existing website.",
    icon: ShieldCheck,
    href: "/en/services/website-redesign",
  },
  {
    title: "Content and enquiry planning",
    body: "A practical route from service explanation to cases, FAQs, WhatsApp, phone or form enquiries.",
    icon: Check,
    href: "/en/services/professional-service-website",
  },
];

export const Route = createFileRoute("/en_/services")({
  head: ({ matches }) => {
    if (matches.some((match) => match.routeId === '/en_/services/$slug')) return {};
    return ({
    meta: [
      { title: "Services | Website Design, SEO and Ads | Doq Creative" },
      {
        name: "description",
        content:
          "Website design, website builds, SEO, Google Ads, Meta Ads and custom ERP systems for Hong Kong businesses.",
      },
      { property: "og:url", content: `${canonicalSiteUrl}/en/services` },
    ],
    links: [{ rel: "canonical", href: `${canonicalSiteUrl}/en/services` }],
    });
  },
  component: EnglishServicesPage,
});

function EnglishServicesPage() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (pathname.replace(/\/$/, '') !== '/en/services') return <Outlet />;

  return (
    <div className="min-h-screen bg-[#f4efe4] text-[#102d28]">
      <div className="mx-auto max-w-[1540px] border-x border-[#143d35]/16 bg-[#f8f3e9]">
        <PublicSiteHeader locale="en" active="services" enHref="/en/services" zhHref="/services" />
        <main id="main-content">
          <section className="border-b border-[#143d35]/14 px-5 py-16 sm:px-8 lg:px-24 lg:py-24">
            <Link to="/en" className="text-sm font-semibold text-[#815321]">
              ← Back home
            </Link>
            <h1 className="mt-10 max-w-4xl text-balance text-5xl font-semibold leading-[1.08] tracking-[-0.04em] sm:text-6xl">
              From website design to useful enquiries
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-[#344840]">
              A connected set of website, search and promotion services for Hong
              Kong SMEs. Start with one need or plan the full enquiry path
              together.
            </p>
          </section>
          <section className="border-b border-[#143d35]/14 px-5 py-12 sm:px-8 lg:px-24 lg:py-16">
            <div className="grid gap-px bg-[#fbf7ef] sm:grid-cols-2 lg:grid-cols-3">
              {services.map((service) => (
                <article
                  key={service.title}
                  className="bg-[#fbf7ef] p-7 sm:p-9"
                >
                  <service.icon
                    className="size-8 text-[#815321]"
                    strokeWidth={1.5}
                  />
                  <h2 className="mt-7 text-2xl font-semibold tracking-[-0.04em]">
                    {service.title}
                  </h2>
                  <p className="mt-4 text-sm leading-7 text-[#344840]">
                    {service.body}
                  </p>
                  <Link
                    to={service.href}
                    className="mt-7 inline-flex items-center gap-2 text-sm font-semibold text-[#173d35] hover:text-[#815321]"
                  >
                    View service
                    <ArrowRight className="size-4" />
                  </Link>
                </article>
              ))}
            </div>
          </section>
          <section className="border-b border-[#143d35]/14 bg-[#f1eadc] px-5 py-12 sm:px-8 lg:px-24 lg:py-16"><div className="grid gap-8 lg:grid-cols-[0.7fr_1.3fr]"><div><p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#815321]">Traffic choice</p><h2 className="mt-4 text-3xl font-semibold tracking-[-0.04em]">SEO vs advertising: what should come first?</h2><p className="mt-5 max-w-xl text-sm leading-7 text-[#344840]">They are not mutually exclusive, but they solve different timing problems. First make sure there is a clear service page and enquiry path, then decide whether long-term visibility or immediate testing is the priority.</p></div><div className="overflow-x-auto"><table className="w-full min-w-[660px] text-left text-sm"><thead className="border-b-2 border-[#143d35]"><tr><th className="py-3 pr-5">Area</th><th className="py-3 pr-5">SEO</th><th className="py-3">Google / Meta Ads</th></tr></thead><tbody className="divide-y divide-[#143d35]/14 text-[#344840]"><tr><th className="py-4 pr-5 text-[#102d28]">Best for</th><td className="py-4 pr-5">Building ongoing search visibility and service content</td><td className="py-4">Testing an offer, message or audience sooner</td></tr><tr><th className="py-4 pr-5 text-[#102d28]">Starting point</th><td className="py-4 pr-5">Clear page structure, content and internal links</td><td className="py-4">A relevant service page or landing page with tracking</td></tr><tr><th className="py-4 pr-5 text-[#102d28]">Data timing</th><td className="py-4 pr-5">Usually needs time to observe search changes</td><td className="py-4">Can show exposure, clicks and enquiries shortly after launch</td></tr><tr><th className="py-4 pr-5 text-[#102d28]">Do not ignore</th><td className="py-4 pr-5">Content and technical updates, not only titles</td><td className="py-4">Landing-page relevance and enquiry quality, not only spend</td></tr></tbody></table></div></div></section>
          <ContactSection
            locale="en"
            title="Not sure where to start?"
            description="Send your current website, industry and goal. We will help you decide between a website, SEO, ads or a focused landing page."
          />
        </main>
      </div>
    </div>
  );
}
